/* ==========================================================================
   Behind The Broadcast — automatic YouTube feed

   Liquid cannot call external APIs, so the newest videos are fetched in the
   browser from the YouTube Data API v3 and rendered into the grid the theme
   already printed. If no key/channel is configured, the request fails, or the
   quota is spent, the manually-entered blocks stay on screen untouched.

   Results are cached in sessionStorage so a browsing session costs one call.
   ========================================================================== */
(function () {
  'use strict';

  const CACHE_MINUTES = 60;

  const fmtViews = (n) => {
    n = parseInt(n, 10);
    if (!n && n !== 0) return '';
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M views';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K views';
    return n + ' views';
  };

  const fmtAgo = (iso) => {
    const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
    if (days < 1) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 30) return days + ' days ago';
    const months = Math.floor(days / 30);
    if (months === 1) return '1 month ago';
    if (months < 12) return months + ' months ago';
    const years = Math.floor(months / 12);
    return years === 1 ? '1 year ago' : years + ' years ago';
  };

  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  async function api(path, params) {
    const url = new URL('https://www.googleapis.com/youtube/v3/' + path);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url);
    if (!res.ok) throw new Error('YouTube API ' + res.status);
    return res.json();
  }

  async function fetchVideos({ key, channel, playlist, count }) {
    const cacheKey = `btb:yt:${channel || playlist}:${count}`;
    try {
      const hit = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (hit && Date.now() - hit.at < CACHE_MINUTES * 60000) return hit.items;
    } catch (e) { /* private mode — just refetch */ }

    let uploads = playlist;
    if (!uploads) {
      const ch = await api('channels', { part: 'contentDetails', id: channel, key });
      uploads = ch.items?.[0]?.contentDetails?.relatedPlaylists?.uploads;
      if (!uploads) throw new Error('channel not found');
    }

    // The API returns at most 50 per request, so asking for more than that used to
    // silently yield 50. Page through until we have `count` or the uploads run out.
    const entries = [];
    let pageToken;
    do {
      const params = {
        part: 'snippet,contentDetails', playlistId: uploads, key,
        maxResults: Math.min(50, count - entries.length)
      };
      if (pageToken) params.pageToken = pageToken;
      const page = await api('playlistItems', params);
      entries.push(...(page.items || []));
      pageToken = page.nextPageToken;
    } while (pageToken && entries.length < count);

    const list = { items: entries.slice(0, count) };

    // `videos` also caps at 50 ids, so request the stats in chunks.
    let stats = {};
    const allIds = list.items.map((i) => i.contentDetails.videoId);
    for (let i = 0; i < allIds.length; i += 50) {
      try {
        const st = await api('videos', { part: 'statistics', id: allIds.slice(i, i + 50).join(','), key });
        st.items.forEach((v) => { stats[v.id] = v.statistics; });
      } catch (e) { /* view counts are optional */ }
    }

    const items = list.items.map((i, idx) => ({
      id: i.contentDetails.videoId,
      title: i.snippet.title,
      published: i.contentDetails.videoPublishedAt || i.snippet.publishedAt,
      thumb: (i.snippet.thumbnails.maxres || i.snippet.thumbnails.high ||
              i.snippet.thumbnails.medium || i.snippet.thumbnails.default || {}).url,
      views: stats[i.contentDetails.videoId]?.viewCount,
      index: idx
    }));

    try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), items })); } catch (e) {}
    return items;
  }

  function cardHTML(v, grid) {
    const page = grid.dataset.style === 'page';
    const url = `https://www.youtube.com/watch?v=${v.id}`;
    const meta = [v.views ? fmtViews(v.views) : '', fmtAgo(v.published)].filter(Boolean).join(' • ');
    const eyebrow = grid.dataset.eyebrowPrefix
      ? `${grid.dataset.eyebrowPrefix} ${grid.dataset.episodeStart - v.index}` : '';

    if (page) {
      return `<a href="${url}" target="_blank" rel="noopener" class="youtube-page-card">
        <div class="youtube-page-card-image">
          <img src="${esc(v.thumb)}" alt="${esc(v.title)}" width="480" height="360" loading="lazy" decoding="async">
        </div>
        <div class="youtube-page-card-info">
          ${eyebrow ? `<span class="yt-page-episode">${esc(eyebrow)}</span>` : ''}
          <h2 class="yt-page-title">${esc(v.title)}</h2>
          ${meta ? `<span class="yt-page-views">${esc(meta)}</span>` : ''}
        </div></a>`;
    }
    return `<a href="${url}" target="_blank" rel="noopener" class="youtube-card">
      <div class="youtube-card-image">
        <img src="${esc(v.thumb)}" alt="${esc(v.title)}" width="480" height="360" loading="lazy" decoding="async">
      </div>
      <div class="youtube-card-info">
        ${eyebrow ? `<span class="yt-episode">${esc(eyebrow)}</span>` : ''}
        <h3 class="yt-title">${esc(v.title)}</h3>
        ${meta ? `<span class="yt-views">${esc(meta)}</span>` : ''}
      </div></a>`;
  }

  async function hydrate(grid) {
    const key = grid.dataset.ytKey;
    const channel = grid.dataset.ytChannel;
    const playlist = grid.dataset.ytPlaylist;
    if (!key || (!channel && !playlist)) return;   // not configured — keep manual blocks

    try {
      const videos = await fetchVideos({
        key, channel, playlist, count: parseInt(grid.dataset.ytCount || '6', 10)
      });
      if (!videos.length) return;
      grid.innerHTML = videos.map((v) => cardHTML(v, grid)).join('');
      grid.dataset.ytLive = 'true';
      document.dispatchEvent(new CustomEvent('btb:youtube:loaded', { detail: { grid, videos } }));
    } catch (e) {
      // Quota, bad key, offline — the manually-entered videos remain.
      console.warn('[BTB] YouTube feed unavailable, showing the configured videos:', e.message);
    }
  }

  const boot = () => document.querySelectorAll('[data-yt-key]').forEach(hydrate);
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
